import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";
import { getTenantSlug } from "./tenantSlug";
import { useAuth } from "./auth";
import {
  REPAIRS_ITEMS, PURGES_ITEMS, PHOTO_PREP_ITEMS,
  EXTERIOR_ITEMS, KITCHEN_ITEMS, LIVING_ROOM_ITEMS, BEDROOMS_ITEMS, BATHROOMS_ITEMS, MISCELLANEOUS_ITEMS,
  TIMELINE_STAGES, VENDORS, CLOSING_CHECKLIST,
  BUYER_TIMELINE_STAGES, BUYER_DATE_FIELDS, BUYER_CLOSING_CHECKLIST,
} from "./data";

export const PREVIEW_KEY = "porchlight_preview_content";
const PREVIEW_SESSION_KEY = "porchlight_preview_session";
export { PREVIEW_SESSION_KEY };

export const isPreviewMode = () => {
  if (typeof window === "undefined") return false;
  // URL param activates preview; sessionStorage keeps it alive across in-tab navigation.
  try {
    if (new URLSearchParams(window.location.search).get("preview") === "1") return true;
    return window.sessionStorage.getItem(PREVIEW_SESSION_KEY) === "1";
  } catch {
    return false;
  }
};

export const clearPreviewSession = () => {
  try { window.sessionStorage.removeItem(PREVIEW_SESSION_KEY); } catch {}
};

export const DEFAULT_CONTENT = {
  landing: {
    hero_overline: "Your home selling companion",
    hero_line_1: "Sell with",
    hero_emphasis: "clarity",
    hero_line_2: ", calm, and confidence.",
    hero_sub: "A guided companion for preparing your home, understanding every step, and submitting your paperwork — all in one warm, simple place.",
    tips: [
      "First impressions matter — focus on curb appeal",
      "Declutter every room before photos",
      "Fix minor repairs to avoid red flags",
      "Price it right from the start",
    ],
  },
  prep: {
    repairs: REPAIRS_ITEMS,
    purges: PURGES_ITEMS,
    photo_prep: PHOTO_PREP_ITEMS,
    exterior: EXTERIOR_ITEMS,
    kitchen: KITCHEN_ITEMS,
    living_room: LIVING_ROOM_ITEMS,
    bedrooms: BEDROOMS_ITEMS,
    bathrooms: BATHROOMS_ITEMS,
    miscellaneous: MISCELLANEOUS_ITEMS,
  },
  prep_blurbs: {
    repairs: "Small fixes that keep buyers focused on the good stuff.",
    purges: "Pack it, donate it, or hide it — buyers imagine their life here.",
    photo_prep: "The morning of photos, every detail matters.",
    exterior: "First impressions start at the curb.",
    kitchen: "The #1 room buyers mentally redecorate. Keep it clean and uncluttered.",
    living_room: "Make it warm, open, and easy to picture their own furniture.",
    bedrooms: "Calm, light, and hotel-neutral beats personal and busy.",
    bathrooms: "Sparkling counters and fresh towels do serious work.",
    miscellaneous: "Everything that doesn't fit a room — closets, storage, laundry.",
  },
  timeline: TIMELINE_STAGES,
  vendors: VENDORS,
  closing: CLOSING_CHECKLIST,
  // Buy-side defaults — mirror the sell-side fields so the /admin/content
  // editor shows a starting point when an agent flips to BUY SIDE, and the
  // buyer-portal renders sensible defaults before any tenant customization.
  buyer_timeline: BUYER_TIMELINE_STAGES,
  buyer_date_fields: BUYER_DATE_FIELDS,
  buyer_closing: BUYER_CLOSING_CHECKLIST,
  // Preferred loan officer card shown at the top of the Buyer Cost
  // Calculator. Each tenant can override with their own preferred lender
  // via /admin/content → BUY SIDE → Lender.
  lender: {
    name: "Sean Knudsen",
    nmls: "265807",
    phone: "801-722-9691",
    email: "sean@vonklending.com",
    title: "Loan Officer",
    company: "Von K Lending",
  },
  // Curated links shown on the BuyerHub Resources tab. Editable in
  // /admin/content → BUY SIDE → Resources.
  buyer_resources: [
    { title: "First-time homebuyer guide (Utah)", url: "https://www.hud.gov/topics/buying_a_home", description: "The federal HUD walk-through of the whole buying process, from budgeting to closing." },
    { title: "How mortgage rates work", url: "https://www.consumerfinance.gov/owning-a-home/loan-options/", description: "Consumer Finance Protection Bureau's plain-English guide to loan options and rates." },
    { title: "Understanding your closing disclosure", url: "https://www.consumerfinance.gov/owning-a-home/closing-disclosure/", description: "Line-by-line walkthrough of the closing document you'll receive 3 days before closing." },
  ],
  // Buyer dashboard copy — editable in /admin/content → BUY SIDE → Dashboard.
  buyer_dashboard: {
    subtitle: "Explore the process, run the numbers, and tell me what you're looking for.",
    card_forms_label: "Intake forms",
    card_process_label: "Buyer process",
    card_upnext_label: "Up next",
    upnext_note: "Once your agent has these, they can start tailoring showings to you.",
    alldone_title: "You're all set.",
    alldone_note: "Both forms are in. Explore the process or run the numbers while your agent lines up showings.",
    quick_access: [
      { title: "Buyer Process", sub: "From pre-approval to keys" },
      { title: "Calculators", sub: "Run the mortgage numbers" },
      { title: "Forms", sub: "Tell me about you" },
      { title: "Vendors", sub: "Trusted local pros" },
    ],
  },
  // Buyer intake forms — fully editable in /admin/content → BUY SIDE → Intake
  // Forms. Each form is an ordered list of sections; each section has a title
  // and a list of fields. Field `type` ∈ text | textarea | tel | number |
  // select (select uses `options`). The buyer forms render straight from here.
  buyer_intake_sections: [
    {
      title: "How to reach you",
      fields: [
        { key: "phone", label: "Best phone number", type: "tel", placeholder: "(555) 123-4567" },
        { key: "preferred_contact", label: "Preferred contact method", type: "select", options: ["Text", "Call", "Email"] },
      ],
    },
    {
      title: "A little background",
      fields: [
        { key: "occupation", label: "Your occupation", type: "text" },
        { key: "household", label: "Who will live in the home with you?", type: "textarea", placeholder: "e.g. spouse, two kids ages 6 & 9" },
        { key: "pets", label: "Pets (type, count)", type: "text", placeholder: "e.g. one golden retriever" },
        { key: "hobbies", label: "Hobbies / how you love to spend time at home", type: "textarea" },
      ],
    },
    {
      title: "One more thing",
      fields: [
        { key: "anything_else", label: "Anything else I should know?", type: "textarea", placeholder: "Optional — life context, timing, special considerations" },
      ],
    },
  ],
  buyer_preferences_sections: [
    {
      title: "The financial picture",
      fields: [
        { key: "budget_min", label: "Budget — minimum", type: "number" },
        { key: "budget_max", label: "Budget — maximum", type: "number" },
        { key: "pre_approved", label: "Are you pre-approved?", type: "select", options: ["Yes", "Working on it", "Not yet"] },
        { key: "timeline", label: "When would you like to be moved in?", type: "text", placeholder: "e.g. by end of summer" },
      ],
    },
    {
      title: "Where & what",
      fields: [
        { key: "preferred_areas", label: "Preferred neighborhoods or areas", type: "textarea" },
        { key: "home_type", label: "Home type", type: "select", options: ["Single Family", "Condo", "Townhouse", "Multi-family", "Open to anything"] },
      ],
    },
    {
      title: "Bedrooms, baths, square footage",
      fields: [
        { key: "beds", label: "Bedrooms (min)", type: "number" },
        { key: "baths", label: "Bathrooms (min)", type: "number" },
        { key: "sqft", label: "Square footage (approx range)", type: "text", placeholder: "e.g. 1800–2400" },
      ],
    },
    {
      title: "What matters most",
      fields: [
        { key: "must_haves", label: "Must-haves", type: "textarea", placeholder: "Non-negotiable features" },
        { key: "nice_to_haves", label: "Nice-to-haves", type: "textarea", placeholder: "Would love but not required" },
        { key: "dealbreakers", label: "Dealbreakers", type: "textarea", placeholder: "No power lines, no busy streets, etc." },
      ],
    },
  ],
  date_fields: [
    { key: "strategy_meeting", label: "Strategy Meeting" },
    { key: "staging", label: "Staging" },
    { key: "cleaners", label: "Cleaners" },
    { key: "photo_day", label: "Photo / Video Day" },
    { key: "listing_launch", label: "Listing Launch" },
    { key: "open_house", label: "Open House" },
    { key: "closing", label: "Closing" },
  ],
  under_contract_fields: [
    { key: "inspection", label: "Inspection", hasTime: true },
    { key: "appraisal", label: "Appraisal", hasTime: true },
    { key: "due_diligence_deadline", label: "Due Diligence Deadline", hasTime: false },
    { key: "finance_appraisal_deadline", label: "Finance & Appraisal Deadline", hasTime: false },
    { key: "settlement_deadline", label: "Settlement Deadline", hasTime: false },
  ],
  // Buyer Welcome Info form (under-contract phase). Each field has a `kind`:
  //   "short" → single-line input on a 2-column grid
  //   "long"  → multi-line textarea, full width
  // Editable globally in Admin → Content, and overridable per seller in
  // Admin → Sellers → (seller) → Buyer Welcome.
  buyer_info_fields: [
    { key: "trash_day",         kind: "short", label: "Trash day",                       placeholder: "Wednesday morning — bin out by 7am" },
    { key: "recycling_day",     kind: "short", label: "Recycling day",                   placeholder: "Every other Friday" },
    { key: "garage_code",       kind: "short", label: "Garage code",                     placeholder: "1234#" },
    { key: "alarm_code",        kind: "short", label: "Alarm / security code",           placeholder: "1234 (master), 5678 (guest)" },
    { key: "mailbox_info",      kind: "short", label: "Mailbox key location / number",   placeholder: "Cluster box #5 — keys in kitchen drawer by the fridge" },
    { key: "hoa_contact",       kind: "short", label: "HOA contact info",                placeholder: "Sentry Mgmt — 801-555-0100, hoa@example.com" },
    { key: "smart_home_notes",  kind: "long",  label: "Smart home / thermostat tips",    hint: "Nest, Ecobee, smart sprinklers, etc.",                                placeholder: "Nest thermostat — 'Eco' mode is set to 65/78. Sprinklers run M/W/F at 6am." },
    { key: "appliance_notes",   kind: "long",  label: "Appliance manuals & warranties",  hint: "Where they live (drawer, binder, garage shelf) plus any active warranties.", placeholder: "All manuals are in the white binder in the pantry. Fridge has a 3-year warranty through 2027." },
    { key: "neighborhood_notes",kind: "long",  label: "Neighborhood tips",               hint: "Best gas station, schools, restaurants, neighbors who'd love an intro.", placeholder: "Best coffee — Mosaic on 9th. Tuesday farmer's market at the park." },
    { key: "other_notes",       kind: "long",  label: "Anything else the buyer should know", hint: "Quirks of the home, where the water shutoff is, breaker tips, etc.", placeholder: "Water shutoff is in the basement utility closet behind the door." },
  ],
  referrals: {
    enabled: true,
    headline: "Thank you — truly.",
    message: "It was a joy guiding you through your sale. If your experience felt as thoughtful as I hoped, a quick review would mean the world and help another neighbor find the same calm.",
    google_url: "",
    zillow_url: "",
  },
  forms: {
    listing_disclaimer: {
      enabled: true,
      title: "A quick note before you start",
      body: "This form is used to market the features of your home on the MLS and other listing platforms, so the more detail you can share, the better. Some fields may have been pre-filled based on the last time the home was sold or public records — please review each section and update anything that's changed so the listing reflects your home today.",
    },
  },
  branding: {
    product_name: "PorchLight",
    agent_name: "",
    agent_first_name: "",
    agent_email: "",
    agent_phone: "",
    firm: "",
    primary_color: "",        // empty = use theme default (forest green)
    logo_data_url: "",        // base64 data URL or http(s) URL; empty = use default home icon
    email_signature: "",      // HTML or plain text; appended to every outgoing email
  },
};

function deepMerge(base, override) {
  if (override == null) return base;
  if (Array.isArray(override)) return override; // arrays replaced wholesale
  if (typeof override !== "object") return override;
  const out = { ...(base || {}) };
  Object.keys(override).forEach((k) => {
    out[k] = deepMerge(base?.[k], override[k]);
  });
  return out;
}

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loaded, setLoaded] = useState(false);
  const [preview, setPreview] = useState(isPreviewMode());
  const [buyerEnabled, setBuyerEnabled] = useState(false);
  const auth = useAuth();
  const activeSide = auth?.user?.active_side || "sell";

  const refresh = useCallback(async () => {
    try {
      // Pass tenant slug from URL so the unauth login screen at
      // theporchlight.io/<slug>/login renders that agent's branding.
      // Backend ignores the slug if the user is authenticated (auth wins).
      const slug = getTenantSlug();
      const url = slug ? `/content?agent_slug=${encodeURIComponent(slug)}` : "/content";
      const { data } = await api.get(url);
      let merged = deepMerge(DEFAULT_CONTENT, data.content || {});
      // Whether THIS tenant is on the buyer beta — drives the buyer vs seller
      // front door (branding + copy) before the visitor even signs in.
      setBuyerEnabled(!!data.buyer_features_enabled);
      if (isPreviewMode()) {
        // Persist preview across in-tab navigation (link clicks drop ?preview=1).
        try { window.sessionStorage.setItem(PREVIEW_SESSION_KEY, "1"); } catch {}
        try {
          const draft = JSON.parse(localStorage.getItem(PREVIEW_KEY) || "null");
          if (draft) merged = deepMerge(merged, draft);
        } catch {}
        setPreview(true);
      }
      setContent(merged);
    } catch {
      setContent(DEFAULT_CONTENT);
    } finally { setLoaded(true); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Adapt content to the user's active side. Every existing page that
  // reads `content.timeline` / `content.date_fields` / `content.closing`
  // gets the RIGHT set for whichever side is on top today, without
  // needing to know buy vs sell. Buy-side pages hide seller-only bits
  // by inspecting `sided.side` directly.
  const sided = useMemo(() => {
    if (activeSide === "buy") {
      // Prefer tenant overrides (set in /admin/content on the BUY SIDE
      // tab) when present, falling back to the code defaults. Empty
      // arrays fall back too so a tenant that saved a blank buyer
      // timeline doesn't leave the buyer with nothing.
      const bTimeline = Array.isArray(content.buyer_timeline) && content.buyer_timeline.length > 0
        ? content.buyer_timeline : BUYER_TIMELINE_STAGES;
      const bDateFields = Array.isArray(content.buyer_date_fields) && content.buyer_date_fields.length > 0
        ? content.buyer_date_fields : BUYER_DATE_FIELDS;
      const bClosing = Array.isArray(content.buyer_closing) && content.buyer_closing.length > 0
        ? content.buyer_closing : BUYER_CLOSING_CHECKLIST;
      return { ...content, side: "buy", timeline: bTimeline, date_fields: bDateFields, closing: bClosing };
    }
    return { ...content, side: "sell" };
  }, [content, activeSide]);

  return (
    <ContentContext.Provider value={{ content: sided, loaded, refresh, preview, buyerEnabled }}>
      {children}
    </ContentContext.Provider>
  );
}

export const useContent = () => useContext(ContentContext) || { content: DEFAULT_CONTENT, loaded: true, refresh: () => {}, preview: false, buyerEnabled: false };
